import React, {
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';

import './OpportunitySourceEditorPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const SOURCES_URL =
  `${API_BASE_URL}/api/v1/opportunity_sources`;

const EMPTY_FORM = {
  name: '',
  base_url: '',
  source_type: 'rss',
  feed_url: '',
  active: true,
  requires_review: true,
  terms_notes: '',
  robots_notes: '',
};

const OpportunitySourceEditorPage = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const isNew = !id;

  const [form, setForm] =
    useState(EMPTY_FORM);

  const [status, setStatus] =
    useState(isNew ? 'ready' : 'loading');

  const [error, setError] =
    useState('');

  useEffect(() => {
    if (isNew) return;

    const loadSource = async () => {
      try {
        const response =
          await axios.get(
            `${SOURCES_URL}/${id}`
          );

        setForm({
          name: response.data.name || '',
          base_url:
            response.data.base_url || '',
          source_type:
            response.data.source_type ||
            'rss',
          feed_url:
            response.data.feed_url || '',
          active:
            Boolean(response.data.active),
          requires_review:
            Boolean(
              response.data.requires_review
            ),
          terms_notes:
            response.data.terms_notes || '',
          robots_notes:
            response.data.robots_notes || '',
        });

        setStatus('ready');
      } catch (requestError) {
        setError(
          requestError.message ||
          'Unable to load source.'
        );

        setStatus('failed');
      }
    };

    loadSource();
  }, [id, isNew]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((current) => ({
      ...current,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));
  };

  const saveSource = async () => {
    try {
      setStatus('saving');
      setError('');

      const payload = {
        opportunity_source: form,
      };

      if (isNew) {
        await axios.post(
          SOURCES_URL,
          payload
        );
      } else {
        await axios.patch(
          `${SOURCES_URL}/${id}`,
          payload
        );
      }

      navigate(
        '/admin/opportunity-sources'
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        requestError.response?.data?.errors
          ?.join(', ') ||
        requestError.message ||
        'Unable to save source.'
      );

      setStatus('ready');
    }
  };

  if (status === 'loading') {
    return (
      <main className="admin-page">
        <p>Loading source...</p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Opportunity monitoring
          </p>

          <h1>
            {isNew
              ? 'Add opportunity source'
              : 'Edit opportunity source'}
          </h1>

          <p>
            Register an approved website,
            RSS feed, API or newsletter used
            for ACE opportunity discovery.
          </p>
        </div>

        <Link
          to="/admin/opportunity-sources"
          className="admin-secondary-link"
        >
          ← Sources
        </Link>
      </header>

      {error && (
        <div className="source-editor-message">
          {error}
        </div>
      )}

      <section className="source-editor">
        <div className="source-editor-grid">
          <label>
            <span>Source name</span>

            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="African Development Bank"
            />
          </label>

          <label>
            <span>Source type</span>

            <select
              name="source_type"
              value={form.source_type}
              onChange={handleChange}
            >
              <option value="rss">
                RSS
              </option>

              <option value="website">
                Website
              </option>

              <option value="api">
                API
              </option>

              <option value="newsletter">
                Newsletter
              </option>
            </select>
          </label>

          <label className="source-editor-full">
            <span>Base URL</span>

            <input
              type="url"
              name="base_url"
              value={form.base_url}
              onChange={handleChange}
              placeholder="https://example.org"
            />
          </label>

          <label className="source-editor-full">
            <span>Feed URL</span>

            <input
              type="url"
              name="feed_url"
              value={form.feed_url}
              onChange={handleChange}
              placeholder="https://example.org/feed/"
            />

            <small>
              Leave blank when the source
              does not provide a dedicated
              feed.
            </small>
          </label>

          <label className="source-editor-full">
            <span>Terms / usage notes</span>

            <textarea
              name="terms_notes"
              value={form.terms_notes}
              onChange={handleChange}
              rows="4"
            />
          </label>

          <label className="source-editor-full">
            <span>
              Robots / monitoring notes
            </span>

            <textarea
              name="robots_notes"
              value={form.robots_notes}
              onChange={handleChange}
              rows="4"
            />
          </label>
        </div>

        <div className="source-editor-options">
          <label>
            <input
              type="checkbox"
              name="active"
              checked={form.active}
              onChange={handleChange}
            />

            <span>Active monitoring source</span>
          </label>

          <label>
            <input
              type="checkbox"
              name="requires_review"
              checked={form.requires_review}
              onChange={handleChange}
            />

            <span>
              Require human review before
              publication
            </span>
          </label>
        </div>

        <footer className="source-editor-actions">
          <button
            type="button"
            onClick={saveSource}
            disabled={status === 'saving'}
          >
            {status === 'saving'
              ? 'Saving...'
              : 'Save source'}
          </button>

          <Link
            to="/admin/opportunity-sources"
          >
            Cancel
          </Link>
        </footer>
      </section>
    </main>
  );
};

export default OpportunitySourceEditorPage;
